// Title Page helpers. The Title Page is not stored separately: it is the title-page
// fields saved on the script itself, so the script area and the editor share one copy.

import { COMMERCIAL_SECTIONS, commercialValue, hasCommercialContent } from './commercialTitlePage';

const AUTO_TITLE = /^Untitled Script( \d+)?$/;

const clean = (value) => (value === undefined || value === null ? '' : String(value).trim());

const escapeHtml = (value) =>
  clean(value).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

// True when the Title Page has real content. An automatic "Untitled Script" name alone counts as blank.
export const hasTitlePageContent = (data = {}) => {
  const title = clean(data.title);
  const realTitle = title && !AUTO_TITLE.test(title);
  if (data.type === 'COMMERCIAL') return Boolean(realTitle || hasCommercialContent(data.commercial || {}));
  const others = ['byline', 'author', 'basedOn', 'copyright', 'email', 'phoneNumber'];
  if (data.type === 'TV') others.push('episodeTitle', 'episodeNumber');
  return Boolean(realTitle || others.some((key) => clean(data[key])));
};

// HTML for the Title Page in the PDF; only filled-in lines are printed.
export const buildTitlePageHtml = (data = {}, pageHeight) => {
  if (data.type === 'COMMERCIAL') return buildCommercialTitlePageHtml(data, pageHeight);
  const title = clean(data.title);
  const lines = [];
  if (title && !AUTO_TITLE.test(title)) lines.push(`<h1 style="text-align:center;">${escapeHtml(title)}</h1>`);
  if (data.type === 'TV') {
    if (clean(data.episodeNumber)) lines.push(`<h2 style="text-align:center;margin-top:16px;">Episode ${escapeHtml(data.episodeNumber)}</h2>`);
    if (clean(data.episodeTitle)) lines.push(`<h2 style="text-align:center;margin-top:16px;">${escapeHtml(data.episodeTitle)}</h2>`);
  }
  if (clean(data.byline)) lines.push(`<h2 style="text-align:center;margin-top:16px;">${escapeHtml(data.byline)}</h2>`);
  if (clean(data.author)) lines.push(`<h2 style="text-align:center;margin-top:16px;">Written by ${escapeHtml(data.author)}</h2>`);
  if (clean(data.basedOn)) lines.push(`<h2 style="text-align:center;margin-top:16px;">Based on ${escapeHtml(data.basedOn)}</h2>`);
  if (clean(data.copyright)) lines.push(`<h2 style="text-align:center;margin-top:16px;">Copyright ${escapeHtml(data.copyright)}</h2>`);

  const contact = [];
  if (clean(data.email)) contact.push(`<p>E-mail: ${escapeHtml(data.email)}</p>`);
  if (clean(data.phoneNumber)) contact.push(`<p>Phone number: ${escapeHtml(data.phoneNumber)}</p>`);

  return `
    <div class="title-page" style="position:relative;padding-top:150px;padding-left:100px;padding-right:100px;height:${pageHeight}px;box-sizing:border-box;">
      ${lines.join('\n')}
      ${contact.length ? `<div style="position:absolute;bottom:0;left:100px;">${contact.join('\n')}</div>` : ''}
    </div>`;
};

// Commercial Title Page: spot title, then each filled-in section as label/value rows.
const buildCommercialTitlePageHtml = (data, pageHeight) => {
  const c = data.commercial || {};
  const title = clean(data.title);
  const parts = [];
  if (title && !AUTO_TITLE.test(title)) parts.push(`<h1 style="text-align:center;margin-bottom:24px;">${escapeHtml(title)}</h1>`);
  COMMERCIAL_SECTIONS.forEach((section) => {
    const rows = section.fields
      .map((f) => [f.label.replace(' (optional)', ''), commercialValue(c, f.key)])
      .filter(([, v]) => v);
    if (!rows.length) return;
    parts.push(`<p style="font-weight:bold;text-decoration:underline;margin:14px 0 4px;">${escapeHtml(section.title.toUpperCase())}</p>`);
    rows.forEach(([label, v]) => parts.push(
      `<p style="margin:2px 0;white-space:pre-wrap;"><span style="font-weight:bold;">${escapeHtml(label)}:</span> ${escapeHtml(v)}</p>`));
  });
  return `
    <div class="title-page" style="position:relative;padding-top:100px;padding-left:100px;padding-right:100px;height:${pageHeight}px;box-sizing:border-box;overflow:hidden;">
      ${parts.join('\n')}
    </div>`;
};
